import { ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { FormsModule } from '@angular/forms';
import { HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { TokenInterceptor } from '@intranet/core/http/token.intercepter';
import { PendingWritesInterceptor } from '@intranet/core/http/pending-writes.interceptor';
import { ChunkLoadErrorHandler } from '@intranet/core/services/chunk-load-error-handler';

import {
    AuthGuard,
    AuthGuardAdmin,
    AuthGuardManager,
    AuthGuardCalendar,
    AuthGuardFinance,
    AuthGuardMonthEnd
} from './core/services/auth-guard.service';

import {
    AuthTokenService,
    NavService,
    RolesService,
} from '@intranet/core/services';

import { SharedModule } from '@intranet/shared/shared.module';

import { HeaderModule } from '@intranet/components/header/header.module';
import { TabsModule } from '@intranet/components/tabs/tabs.module';
import { TooltipModule } from '@intranet/components/tooltip/tooltip.module';
import { OverlayModule } from '@intranet/components/overlay/overlay.module';
import { NavModule } from '@intranet/components/navmenu/nav.module';
import { DialogModule } from '@intranet/components/dialog/dialog.module';
import { CrashRecoveryModalModule } from '@intranet/modules/desktop/crash-recovery-modal/crash-recovery-modal.module';
import { TimerToastModule } from '@intranet/modules/timer/timer-toast/timer-toast.module';

import { AppComponent } from './app.component';
import { UIService } from '@intranet/core/services/ui.service';
import { MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { LayoutDefaultComponent } from './layout/default/default.component';
import { LayoutEmptyComponent } from './layout/empty/empty.component';
import { LayoutComponent } from './layout/layout.component';
import { ExtraOptions, PreloadAllModules, RouterModule } from '@angular/router';
import { appRoutes } from './app.routes';
import { DatePipe } from '@angular/common';
import { CalendarService } from './core/services/calendar.service';
import { PipesModule } from './core/pipes/pipes.module';

const routerConfig: ExtraOptions = {
    preloadingStrategy: PreloadAllModules,
    scrollPositionRestoration: 'enabled',
    enableTracing: false,
};

@NgModule({
    declarations: [
        AppComponent,
        LayoutComponent,
        LayoutDefaultComponent,
        LayoutEmptyComponent,
    ],
    imports: [
        BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
        HttpClientModule,
        BrowserAnimationsModule,
        RouterModule.forRoot(appRoutes, routerConfig),
        FormsModule,
        PipesModule,
        SharedModule,
        TooltipModule,
        OverlayModule,
        HeaderModule,
        NavModule,
        TabsModule,
        DialogModule,
        CrashRecoveryModalModule,
        TimerToastModule,
    ],
    providers: [
        UIService,
        NavService,
        RolesService,
        CalendarService,
        AuthGuard,
        AuthGuardAdmin,
        AuthGuardManager,
        AuthGuardCalendar,
        AuthGuardFinance,
        AuthGuardMonthEnd,
        AuthTokenService,
        DatePipe,
        // Listed before TokenInterceptor so a write is counted as soon as it
        // starts, including while its auth token is still being fetched.
        {
            provide: HTTP_INTERCEPTORS,
            useClass: PendingWritesInterceptor,
            multi: true,
        },
        {
            provide: HTTP_INTERCEPTORS,
            useClass: TokenInterceptor,
            multi: true,
        },
        // Auto-recover from webpack chunk-load failures (typically caused by
        // a stale Chromium cache pointing at chunk filenames the latest
        // server deploy no longer has). Without this, users land on a grey
        // screen and have to know to hard-refresh — see comments in the
        // handler file for the full story.
        { provide: ErrorHandler, useClass: ChunkLoadErrorHandler },
        { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
        {
            provide: MAT_DIALOG_DEFAULT_OPTIONS,
            useValue: { hasBackdrop: true },
        },
    ],
    bootstrap: [AppComponent],
})
export class AppModule {}
